/**
 * @module components/TableFieldSetting/UrlSetting
 * @description 表格图表 — 维度字段 URL 格式设置
 *
 * ## 状态读写
 *
 * - 读/写配置：`useSettingObjectState('field_setting')`
 * - 读维度列表：`useQueryColumnDimensions()`（必须用专用 hook，见下）
 *
 * ## 为何不用通用 useQuerySelector 读 columnDimensions？
 *
 * Analysis model 重命名时原地 mutate IColumnInfo，shallowEqual 认为列表未变，
 * prefix（column_display）不会刷新；且 QB 联动无法触发 field_setting 的 migrate。
 * 详见 hooks/dva/useQueryColumnLists.ts。
 *
 * ## QB 重命名与 field_setting 联动（用户无感）
 *
 * - 表字段改 display：key 不变，本组件仅 prefix 更新
 * - 自定义字段改 column_name：fieldSettingMigrateHandler 迁移 key（base+draft）
 */

import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'umi';
import ConfigCollapseCard from '@/pages/datalab/components/ConfigCollapseCard';
import SelectWithButton from '@/pages/datalab/components/SelectWithButton';
import type { IColumnInfo } from '@dbl/core/dataSource';
import type { IColumnFilter } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import { useSettingObjectState } from '../../context';
import { useQueryColumnDimensions, useQuerySelector } from '../../hooks/deps';
import { EMPTY_COLUMN_INFO_LIST } from '../../utils/constants';
import type { IFieldSetting } from '../../types/settings/fieldSetting';
import {
  FieldShowType,
  type IFieldSettingItem,
} from '../../types/settings/fieldSetting';

export { FieldShowType, type IFieldSetting, type IFieldSettingItem };

const UrlSetting: React.FC = () => {
  const { formatMessage } = useIntl();
  const { value, setValue } = useSettingObjectState('field_setting');
  const columnFilters = useQuerySelector(
    s => s.columnFilters ?? EMPTY_COLUMN_INFO_LIST,
  );
  const columnDimensions = useQueryColumnDimensions();

  const { compareFilters, isHidden, group } = useMemo(() => {
    const compareFilters = columnFilters.find(item => {
      const filter = item as IColumnFilter;
      return filter.compareTimes && filter.compareTimes.length > 0;
    }) as IColumnFilter | undefined;
    const isHidden =
      columnDimensions.length === 1 &&
      columnDimensions[0]?.id === compareFilters?.id;
    return { compareFilters, isHidden, group: columnDimensions };
  }, [columnFilters, columnDimensions]);

  const options = useMemo(
    () => [
      {
        value: FieldShowType.Original,
        text: formatMessage({ id: 'dbl_chart_field_setting_original' }),
      },
      {
        value: FieldShowType.Url,
        text: formatMessage({ id: 'dbl_chart_field_setting_url' }),
      },
    ],
    [formatMessage],
  );

  const createChange = useCallback(
    (item: IColumnInfo) => (selectValue: FieldShowType) => {
      const key = `${item.table_code || 't0'}_${item.column_name}`;
      const nextValue: IFieldSetting = {
        ...value,
        [key]: {
          column_name: item.column_name,
          table_id: item.table_id,
          table_code: item.table_code || 't0',
          format: selectValue,
          column_role: 1,
        },
      };
      setValue(nextValue);
    },
    [value, setValue],
  );

  if (isHidden) {
    return null;
  }

  return (
    <ConfigCollapseCard
      title="URL"
      className="dbl-config-collapse-card-h2 dbl-table-field-url-setting"
    >
      {group.map((item: IColumnInfo) => {
        if (compareFilters?.id === item?.id) {
          return null;
        }
        const key = `${item.table_code || 't0'}_${item.column_name}`;
        return (
          <SelectWithButton
            key={item?.id}
            prefix={item.column_display}
            appearance="button"
            value={value[key]?.format || FieldShowType.Original}
            overlayStyle={{ width: 302 }}
            options={options}
            matchButtonWidth
            onChange={createChange(item)}
          />
        );
      })}
    </ConfigCollapseCard>
  );
};

export default React.memo(UrlSetting);
